import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BadgeCheck,
  KeyRound,
  Pencil,
  Plus,
  ShieldCheck,
  Trash2,
  UserMinus,
  UserSquare2,
  Wallet,
} from 'lucide-react';
import {
  EMPLOYMENT_STATUSES,
  EMPLOYMENT_STATUS_LABELS,
  MEMBER_ROLE_LABELS,
  type EmploymentStatus,
  type Paginated,
  type Staff,
} from '@cenox/shared';
import { ApiError, api, qs } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { date, money } from '../../lib/format';
import {
  ActionMenu,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Input,
  MenuItem,
  MenuSeparator,
  Modal,
  Pagination,
  Select,
  TableSkeleton,
  Toaster,
  useToasts,
} from '../../components/ui';
import { PageHeader } from '../../layouts/AppShell';
import {
  AccessDialog,
  EditStaffDialog,
  PermissionsDialog,
  SalaryDialog,
} from './StaffActions';

const STATUS_TONES: Record<EmploymentStatus, 'success' | 'warning' | 'neutral'> = {
  active: 'success',
  on_leave: 'warning',
  terminated: 'neutral',
};

/** Which dialog a row has opened, if any. */
type Dialog = 'edit' | 'access' | 'permissions' | 'salary' | 'deactivate' | 'delete';

interface Branch {
  id: string;
  name: string;
}

export function StaffPage(): ReactNode {
  const { business, can } = useAuth();
  const path = useBusinessPath();
  const queryClient = useQueryClient();
  const { toasts, show, dismiss } = useToasts();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('');
  /*
    Active by default, not "everyone".

    Deactivating somebody is supposed to take them out of the list — that is
    most of what it is for — so the list opens on the people who work here now.
    "All status" is one click away, and the filter shows which one is on, so
    nobody is left wondering where a name went.
  */
  const [status, setStatus] = useState<EmploymentStatus | 'all'>('active');
  const [creating, setCreating] = useState(false);
  const [acting, setActing] = useState<{ member: Staff; dialog: Dialog } | null>(null);

  const filters = qs({
    page,
    pageSize: 25,
    search: search || undefined,
    roleTitle: role || undefined,
    employmentStatus: status === 'all' ? undefined : status,
  });

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['staff', business?.businessId, filters],
    queryFn: () => api.get<Paginated<Staff>>(path(`/staff${filters}`)),
    enabled: Boolean(business),
  });

  // The job titles actually in use, so the filter offers real answers.
  const { data: roles } = useQuery({
    queryKey: ['staff', 'roles', business?.businessId],
    queryFn: () => api.get<{ data: string[] }>(path('/staff/roles')),
    enabled: Boolean(business),
  });

  // For the location field on the edit form. Only fetched for someone who can
  // actually edit, so a read-only viewer does not call it for nothing.
  const { data: branches } = useQuery({
    queryKey: ['branches', business?.businessId],
    queryFn: () => api.get<{ data: Branch[] }>(path('/branches')),
    enabled: Boolean(business) && can('staff:update'),
  });

  const timezone = business?.timezone ?? 'UTC';
  const currency = business?.currencyCode ?? 'USD';

  function refresh(message: string): void {
    void queryClient.invalidateQueries({ queryKey: ['staff'] });
    void queryClient.invalidateQueries({ queryKey: ['members'] });
    setActing(null);
    show(message);
  }

  /*
    One mutation per row action rather than one per row: the row the action
    belongs to is held in `acting`, so the list can re-render freely while a
    request is in flight without resetting it.
  */
  const deactivate = useMutation({
    mutationFn: (member: Staff) => api.post<Staff>(path(`/staff/${member.id}/deactivate`), {}),
    onSuccess: (_result, member) =>
      refresh(`${member.fullName} deactivated. Their dashboard access is switched off.`),
    onError: (caught) =>
      show(caught instanceof ApiError ? caught.message : 'Could not deactivate them.', 'error'),
  });

  const reactivate = useMutation({
    mutationFn: (member: Staff) => api.post<Staff>(path(`/staff/${member.id}/reactivate`), {}),
    onSuccess: (_result, member) => refresh(`${member.fullName} is active again.`),
    onError: (caught) =>
      show(caught instanceof ApiError ? caught.message : 'Could not reactivate them.', 'error'),
  });

  const remove = useMutation({
    mutationFn: (member: Staff) => api.del(path(`/staff/${member.id}`)),
    onSuccess: (_result, member) =>
      refresh(`${member.fullName} deleted. Their past records are kept.`),
    onError: (caught) =>
      show(caught instanceof ApiError ? caught.message : 'Could not delete them.', 'error'),
  });

  /** The menu for one row, or nothing when this person may do none of it. */
  function rowActions(member: Staff): ReactNode {
    const mayEdit = can('staff:update');
    const mayManage = can('staff:manage');
    const mayDelete = can('staff:delete');
    const mayAccess = can('members:invite') || can('members:update');
    const mayPermit = can('members:read') && can('members:update') && Boolean(member.memberId);
    if (!mayEdit && !mayManage && !mayDelete && !mayAccess) return null;

    const open = (dialog: Dialog) => () => setActing({ member, dialog });

    return (
      <ActionMenu label={`Actions for ${member.fullName}`}>
        {mayEdit ? (
          <MenuItem icon={<Pencil className="size-4" />} onSelect={open('edit')}>
            Edit staff
          </MenuItem>
        ) : null}
        {mayAccess ? (
          <MenuItem icon={<KeyRound className="size-4" />} onSelect={open('access')}>
            Set login &amp; access
          </MenuItem>
        ) : null}
        {mayPermit ? (
          <MenuItem icon={<ShieldCheck className="size-4" />} onSelect={open('permissions')}>
            Manage permissions
          </MenuItem>
        ) : null}
        {mayEdit ? (
          <MenuItem icon={<Wallet className="size-4" />} onSelect={open('salary')}>
            Update salary
          </MenuItem>
        ) : null}

        {mayManage || mayDelete ? <MenuSeparator /> : null}

        {mayManage ? (
          member.employmentStatus === 'active' ? (
            <MenuItem icon={<UserMinus className="size-4" />} onSelect={open('deactivate')}>
              Deactivate staff
            </MenuItem>
          ) : (
            <MenuItem
              icon={<BadgeCheck className="size-4" />}
              onSelect={() => reactivate.mutate(member)}
            >
              Reactivate staff
            </MenuItem>
          )
        ) : null}
        {mayDelete ? (
          <MenuItem tone="danger" icon={<Trash2 className="size-4" />} onSelect={open('delete')}>
            Delete staff
          </MenuItem>
        ) : null}
      </ActionMenu>
    );
  }

  /** Dashboard access, including the state deactivating leaves behind. */
  function accessBadge(member: Staff): ReactNode {
    if (!member.memberRole) return <span className="text-slate-400">No login</span>;
    if (member.memberStatus === 'suspended') return <Badge tone="neutral">Access off</Badge>;
    if (member.memberStatus === 'invited') return <Badge tone="warning">Invited</Badge>;
    return <Badge tone="info">{MEMBER_ROLE_LABELS[member.memberRole]}</Badge>;
  }

  const filtered = Boolean(search || role || status !== 'active');

  return (
    <>
      <PageHeader
        title="Staff"
        description="Everyone who works for the business — with or without a login"
        action={
          can('staff:create') && (
            <Button icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
              Add staff
            </Button>
          )
        }
      />

      <Card className="mb-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-[minmax(0,1fr)_11rem_11rem]">
          <Input
            placeholder="Search by name, phone or role…"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            aria-label="Search staff"
          />
          <Select
            aria-label="Filter by role"
            value={role}
            onChange={(event) => {
              setRole(event.target.value);
              setPage(1);
            }}
            placeholder="All roles"
            options={(roles?.data ?? []).map((title) => ({ value: title, label: title }))}
          />
          <Select
            aria-label="Filter by status"
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as EmploymentStatus | 'all');
              setPage(1);
            }}
            options={[
              { value: 'all', label: 'All status' },
              ...EMPLOYMENT_STATUSES.map((value) => ({
                value,
                label: EMPLOYMENT_STATUS_LABELS[value],
              })),
            ]}
          />
        </div>
      </Card>

      <Card padded={false}>
        {isPending ? (
          <TableSkeleton columns={4} />
        ) : error ? (
          <div className="p-4">
            <ErrorState message="Could not load staff." onRetry={() => refetch()} />
          </div>
        ) : data.data.length === 0 ? (
          <EmptyState
            icon={<UserSquare2 className="size-10" />}
            title={filtered ? 'No staff match these filters' : 'No staff recorded yet'}
            description={
              filtered
                ? 'Try a different search, role or status — deactivated staff are under “All status”.'
                : 'Add your team to attribute expenses and track payroll. A staff member does not need a login.'
            }
            action={
              can('staff:create') && !filtered ? (
                <Button icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
                  Add staff
                </Button>
              ) : undefined
            }
          />
        ) : (
          <>
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 bg-slate-50">
                  <tr>
                    <th scope="col" className="px-4 py-2.5 text-left text-xs font-medium text-slate-600">Name</th>
                    <th scope="col" className="px-4 py-2.5 text-left text-xs font-medium text-slate-600">Role</th>
                    <th scope="col" className="px-4 py-2.5 text-left text-xs font-medium text-slate-600">Phone</th>
                    <th scope="col" className="px-4 py-2.5 text-left text-xs font-medium text-slate-600">Status</th>
                    <th scope="col" className="px-4 py-2.5 text-left text-xs font-medium text-slate-600">Dashboard access</th>
                    <th scope="col" className="px-4 py-2.5 text-right text-xs font-medium text-slate-600">Salary</th>
                    <th scope="col" className="px-4 py-2.5 text-left text-xs font-medium text-slate-600">Hired</th>
                    <th scope="col" className="w-12 px-2 py-2.5">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.data.map((member) => (
                    <tr key={member.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium text-slate-900">{member.fullName}</td>
                      <td className="px-4 py-3 text-slate-600">{member.roleTitle ?? '—'}</td>
                      <td className="px-4 py-3 text-slate-600">{member.phone ?? '—'}</td>
                      <td className="px-4 py-3">
                        <Badge tone={STATUS_TONES[member.employmentStatus]}>
                          {EMPLOYMENT_STATUS_LABELS[member.employmentStatus]}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">{accessBadge(member)}</td>
                      <td className="px-4 py-3 text-right tabular">
                        {member.salaryAmount ? money(member.salaryAmount, currency) : '—'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-600">
                        {member.hiredAt
                          ? date(`${member.hiredAt}T00:00:00Z`, timezone)
                          : '—'}
                      </td>
                      <td className="px-2 py-3 text-right">{rowActions(member)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <ul className="divide-y divide-slate-100 md:hidden">
              {data.data.map((member) => (
                <li key={member.id} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-medium text-slate-900">{member.fullName}</p>
                      <p className="mt-0.5 text-xs text-slate-500">
                        {member.roleTitle ?? 'No role set'}
                        {member.phone && ` · ${member.phone}`}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      {member.salaryAmount && (
                        <p className="text-sm font-medium tabular text-slate-700">
                          {money(member.salaryAmount, currency)}
                        </p>
                      )}
                      {rowActions(member)}
                    </div>
                  </div>
                  <div className="mt-2.5 flex flex-wrap items-center gap-2">
                    <Badge tone={STATUS_TONES[member.employmentStatus]}>
                      {EMPLOYMENT_STATUS_LABELS[member.employmentStatus]}
                    </Badge>
                    {accessBadge(member)}
                  </div>
                </li>
              ))}
            </ul>

            <Pagination
              page={data.meta.page}
              pageSize={data.meta.pageSize}
              total={data.meta.total}
              onPageChange={setPage}
            />
          </>
        )}
      </Card>

      <Modal open={creating} onClose={() => setCreating(false)} title="Add a staff member">
        <StaffForm
          onDone={() => {
            setCreating(false);
            void queryClient.invalidateQueries({ queryKey: ['staff'] });
            void queryClient.invalidateQueries({ queryKey: ['staff', 'roles'] });
          }}
          onCancel={() => setCreating(false)}
        />
      </Modal>

      {acting?.dialog === 'edit' ? (
        <EditStaffDialog
          member={acting.member}
          branches={branches?.data ?? []}
          onClose={() => setActing(null)}
          onSaved={refresh}
        />
      ) : null}

      {acting?.dialog === 'access' ? (
        <AccessDialog
          member={acting.member}
          onClose={() => setActing(null)}
          onSaved={refresh}
        />
      ) : null}

      {acting?.dialog === 'permissions' ? (
        <PermissionsDialog
          member={acting.member}
          onClose={() => setActing(null)}
          onSaved={refresh}
        />
      ) : null}

      {acting?.dialog === 'salary' ? (
        <SalaryDialog member={acting.member} onClose={() => setActing(null)} onSaved={refresh} />
      ) : null}

      <ConfirmDialog
        open={acting?.dialog === 'deactivate'}
        onClose={() => setActing(null)}
        onConfirm={() => acting && deactivate.mutate(acting.member)}
        title="Deactivate this staff member?"
        confirmLabel="Deactivate"
        loading={deactivate.isPending}
        description={
          <>
            <p>
              {acting?.member.fullName} will be marked as no longer employed and taken out of the
              staff list. If they have a login, it stops working immediately.
            </p>
            <p className="mt-2 text-slate-500">
              Nothing is deleted — their deliveries, expenses, payroll and history stay exactly as
              they are, and you can reactivate them at any time.
            </p>
          </>
        }
      />

      <ConfirmDialog
        open={acting?.dialog === 'delete'}
        onClose={() => setActing(null)}
        onConfirm={() => acting && remove.mutate(acting.member)}
        title="Are you sure you want to delete this staff member?"
        confirmLabel="Delete staff member"
        loading={remove.isPending}
        description={
          <>
            <p>
              {acting?.member.fullName} will be removed from the staff list, and their login — if
              they have one — is switched off.
            </p>
            <p className="mt-2 text-slate-500">
              Their history is kept. Every record that names them — deliveries, expenses they are
              attributed to, salary records, the audit trail — stays as it is. If you only want to
              stop them working here for now, use Deactivate instead.
            </p>
          </>
        }
      />

      <Toaster toasts={toasts} onDismiss={dismiss} />
    </>
  );
}

function StaffForm({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }): ReactNode {
  const { business } = useAuth();
  const path = useBusinessPath();

  const [form, setForm] = useState({
    fullName: '',
    phone: '',
    email: '',
    roleTitle: '',
    employmentStatus: 'active' as EmploymentStatus,
    salaryAmount: '',
    hiredAt: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const update =
    <K extends keyof typeof form>(key: K) =>
    (event: { target: { value: string } }) =>
      setForm((current) => ({ ...current, [key]: event.target.value }));

  const create = useMutation({
    mutationFn: (payload: Record<string, unknown>) => api.post(path('/staff'), payload),
    onSuccess: onDone,
    onError: (caught) => {
      if (caught instanceof ApiError) {
        setError(caught.message);
        if (caught.fields) {
          setFieldErrors(
            Object.fromEntries(
              Object.entries(caught.fields).map(([key, messages]) => [key, messages[0] ?? '']),
            ),
          );
        }
      } else {
        setError('Could not add the staff member.');
      }
    },
  });

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    create.mutate({
      fullName: form.fullName,
      phone: form.phone || null,
      email: form.email || null,
      roleTitle: form.roleTitle || undefined,
      employmentStatus: form.employmentStatus,
      salaryAmount: form.salaryAmount || null,
      hiredAt: form.hiredAt || null,
    });
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      {error && !Object.keys(fieldErrors).length && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <Input
        label="Full name"
        required
        autoFocus
        value={form.fullName}
        onChange={update('fullName')}
        error={fieldErrors.fullName}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          label="Phone"
          type="tel"
          value={form.phone}
          onChange={update('phone')}
          error={fieldErrors.phone}
        />
        <Input
          label="Job title"
          value={form.roleTitle}
          onChange={update('roleTitle')}
          error={fieldErrors.roleTitle}
          placeholder="e.g. Rider, Cook, Cashier"
        />
      </div>

      <Input
        label="Email"
        type="email"
        value={form.email}
        onChange={update('email')}
        error={fieldErrors.email}
        hint="Optional. Adding a login is done separately, in Settings → Team."
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Select
          label="Employment status"
          options={EMPLOYMENT_STATUSES.map((value) => ({
            value,
            label: EMPLOYMENT_STATUS_LABELS[value],
          }))}
          value={form.employmentStatus}
          onChange={update('employmentStatus')}
        />
        <Input
          label={`Salary (${business?.currencyCode ?? ''})`}
          inputMode="decimal"
          value={form.salaryAmount}
          onChange={update('salaryAmount')}
          error={fieldErrors.salaryAmount}
          hint="Optional"
        />
      </div>

      <Input
        label="Hired on"
        type="date"
        value={form.hiredAt}
        onChange={update('hiredAt')}
        error={fieldErrors.hiredAt}
      />

      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" loading={create.isPending}>
          Add staff member
        </Button>
      </div>
    </form>
  );
}
